import * as Sentry from '@sentry/react';
import { GW_SERVICES_PREFIXES } from 'constants/gateway';
import { SocketEvents, SocketEventsKey } from './events';
import { SocketEventHandler, SocketOptions } from './socket.types';
import {
  SOCKET_CLOSE_CODES,
  SOCKET_CLOSE_CODES_NO_RECONNECT,
  SocketCloseCode,
} from './socket.constants';
import { isConnectionLimitError } from './socket.utils';
import { refresh_tokens_mutex } from 'utils/request';
import { tokenRefreshCoordinator } from 'utils/token-refresh-coordinator';

const { AUTHORIZATION } = GW_SERVICES_PREFIXES;

export class AppSocket {
  private static instances: Map<string, AppSocket> = new Map();
  private ws: WebSocket | null = null;
  private subscriptions: Set<keyof SocketEvents> = new Set();
  private sendedSubscriptions: Set<keyof SocketEvents> = new Set();
  private listeners: Map<string, Set<SocketEventHandler>> = new Map();
  private queue: string[] = [];
  private url: string;
  private debug: boolean;
  private reconnectAttempts: number = 0;
  private reconnectTimeout: number | null = null;
  private isConnected: boolean = false;
  private manualDisconnect = false; // флаг ручного закрытия
  private connectionLimitReached = false; // флаг достижения лимита соединений
  private connectCallbacks: Set<() => void> = new Set();

  // retry reconnect
  private readonly baseReconnectDelay = 1_000; // 1 секунда
  private readonly maxReconnectDelay = 30_000; // 30 секунд
  private readonly maxReconnectAttempts = 10; // лимит на 10 попыток

  // heartbeat
  private heartbeatInterval: number | null = null;
  private readonly heartbeatDelay = 15_000; // каждые 15 секунд
  private readonly heartbeatTimeout = 5_000; // ждём pong не дольше 5 сек
  private pongTimeout: number | null = null;

  private constructor({ url, debug }: SocketOptions) {
    this.url = url;
    this.debug = debug ?? false;

    // При активации вкладки пытаемся переподключиться если был лимит
    document.addEventListener('visibilitychange', () => {
      if (
        !document.hidden &&
        this.connectionLimitReached &&
        !this.isConnected &&
        !this.manualDisconnect
      ) {
        this.debug &&
          console.log(
            '[AppSocket] вкладка активирована, пробуем переподключиться после лимита'
          );
        this.connectionLimitReached = false;
        this.connect();
      }
    });
  }

  static getInstance(options?: SocketOptions) {
    if (!options) {
      throw new Error('AppSocket не инициализирован');
    }

    const key = options.url;

    if (!AppSocket.instances.has(key)) {
      AppSocket.instances.set(key, new AppSocket(options));
    }

    return AppSocket.instances.get(key)!;
  }

  connect() {
    // Не создаём новое соединение если уже подключены или в процессе
    if (
      this.ws &&
      (this.isConnected || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      this.debug &&
        console.log(
          '[AppSocket] соединение уже существует, пропускаем connect()'
        );

      return;
    }

    this.manualDisconnect = false;

    const wsProtocol = window.location.protocol === 'http:' ? 'ws:' : 'wss:';
    const url = `${wsProtocol}//${window.location.host}/${this.url}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      if (this.isConnected) {
        return;
      }

      this.isConnected = true;
      this.debug && console.log('[AppSocket] подключено');
      this.reconnectAttempts = 0;
      this.resubscribe();
      this.flushQueue();
      this.connectCallbacks.forEach((cb) => cb());
      this.connectCallbacks.clear();
      this.startHeartbeat();
    };

    this.ws.onmessage = (e) => this.handleMessage(e.data);

    this.ws.onclose = async (event: CloseEvent) => {
      this.isConnected = false;
      this.sendedSubscriptions.clear();
      this.stopHeartbeat();

      if (this.debug) {
        console.log(
          `[AppSocket] отключено. Код: ${event.code}, причина: ${
            event.reason || '—'
          }, чисто?: ${event.wasClean}`,
          event
        );
      }

      if (this.manualDisconnect) {
        return;
      }

      // Лимит соединений — не пытаемся переподключаться
      if (this.connectionLimitReached) {
        this.debug &&
          console.log(
            '[AppSocket] лимит соединений достигнут, переподключение отменено'
          );

        return;
      }

      if (event.code === SOCKET_CLOSE_CODES.TOKEN_EXPIRED) {
        this.debug &&
          console.log('[AppSocket] токен истёк, выполняем refresh...');

        try {
          await this.refreshToken();
          this.connect();

          this.debug &&
            console.log('[AppSocket] токен обновлён, переподключаемся');
        } catch (err) {
          this.debug &&
            console.error(
              '[AppSocket] не удалось обновить токен, соединение закрыто',
              err
            );
          this.connectCallbacks.clear();
        }

        return;
      } else if (
        SOCKET_CLOSE_CODES_NO_RECONNECT.includes(event.code as SocketCloseCode)
      ) {
        this.debug &&
          console.log(
            '[AppSocket] сервер закрыл соединение, повторного подключения не будет'
          );
        this.connectCallbacks.clear();

        return;
      } else if (
        this.connectionLimitReached ||
        event.code === SOCKET_CLOSE_CODES.CONNECTION_LIMIT ||
        isConnectionLimitError(event.reason)
      ) {
        // Флаг мог быть установлен в handleMessage до закрытия
        if (!this.connectionLimitReached) {
          this.connectionLimitReached = true;
          const message = `[AppSocket] Лимит WebSocket соединений исчерпан. Закройте лишние вкладки. Код: ${event.code}, причина: ${event.reason}`;
          console.error(message);
          Sentry.captureMessage(message, 'warning');
        }

        return;
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = (error) =>
      this.debug && console.error('[AppSocket] ошибка', error);
  }

  disconnect() {
    this.manualDisconnect = true;
    this.connectCallbacks.clear();
    this.stopHeartbeat();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Disconnect by client');
      this.ws = null;
      this.isConnected = false;
      this.debug && console.log('[AppSocket] отключение вручную');
    }
  }

  subscribe(event: SocketEventsKey | SocketEventsKey[]) {
    const events = Array.isArray(event) ? event : [event];

    events.forEach((e) => {
      if (!this.subscriptions.has(e)) {
        this.subscriptions.add(e);

        // сразу же отправляем, если сокет открыт
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.send({ event: e, action: 'subscribe' });
          this.sendedSubscriptions.add(e);
        }
      }
    });
  }

  unsubscribe(event: SocketEventsKey | SocketEventsKey[]) {
    const events = Array.isArray(event) ? event : [event];

    events.forEach((e) => {
      if (this.subscriptions.has(e)) {
        this.subscriptions.delete(e);
        this.sendedSubscriptions.delete(e); // удаляем из отправленных

        if (this.ws?.readyState === WebSocket.OPEN) {
          this.send({ event: e, action: 'unsubscribe' });
        }
      }
    });
  }

  send(payload: unknown) {
    const message = JSON.stringify(payload);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.debug && console.log('[AppSocket] достигнут лимит переподключений');
      this.connectCallbacks.clear();

      return;
    }

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    this.reconnectAttempts++;

    this.debug &&
      console.log(
        `[AppSocket] переподключение #${this.reconnectAttempts} через ${delay}мс`
      );

    this.reconnectTimeout = window.setTimeout(() => this.connect(), delay);
  }

  private async refreshToken(): Promise<void> {
    // Координация между вкладками через localStorage
    if (!tokenRefreshCoordinator.tryAcquireLock()) {
      // Другая вкладка уже делает refresh — ждём
      this.debug &&
        console.log('[AppSocket] Ждём refresh от другой вкладки...');
      await tokenRefreshCoordinator.waitForRefresh();

      return;
    }

    // Mutex для синхронизации внутри одной вкладки (с HTTP запросами)
    const release = await refresh_tokens_mutex.acquire();

    try {
      const response = await fetch(`/${AUTHORIZATION}/auth/refresh/`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      // Уведомляем другие вкладки об успешном refresh
      tokenRefreshCoordinator.notifyRefreshComplete();
    } catch (error) {
      tokenRefreshCoordinator.releaseLock();

      throw error;
    } finally {
      release();
    }
  }

  private resubscribe() {
    if (!this.subscriptions.size) {
      return;
    }

    this.subscriptions.forEach((event) => {
      if (!this.sendedSubscriptions.has(event)) {
        if (this.debug) {
          console.log('[AppSocket] восстановление подписки:', event);
        }

        this.send({ event, action: 'subscribe' });
        this.sendedSubscriptions.add(event);
      }
    });
  }

  private flushQueue() {
    while (this.queue.length && this.ws?.readyState === WebSocket.OPEN) {
      const msg = this.queue.shift();

      if (msg) {
        this.ws.send(msg);
      }
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat();

    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) {
        return;
      }

      this.debug && console.log('[AppSocket] -> ping');
      this.send({ action: 'ping' });

      this.pongTimeout = window.setTimeout(() => {
        this.debug &&
          console.log('[AppSocket] pong timeout, переподключаемся...');
        this.ws?.close();
      }, this.heartbeatTimeout);
    }, this.heartbeatDelay);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  on<K extends keyof SocketEvents>(
    event: K,
    handler: SocketEventHandler<SocketEvents[K]>
  ) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(handler);
    this.debug && console.log('[AppSocket] подписаны на событие:', event);

    return () => this.off(event, handler);
  }

  onMany<K extends keyof SocketEvents>(
    events: K[],
    handler: (
      event: {
        [P in K]: { event: P; payload: SocketEvents[P] };
      }[K]
    ) => void
  ) {
    const unsubscribers = events.map((event) =>
      this.on(event, (payload) => handler({ event, payload }))
    );

    return () => {
      unsubscribers.forEach((u) => u());
    };
  }

  private off<K extends keyof SocketEvents>(
    event: K,
    handler: SocketEventHandler<SocketEvents[K]>
  ) {
    this.listeners.get(event)?.delete(handler);
    this.debug && console.log('[AppSocket] отписаны от события:', event);
  }

  private handleMessage(data: string) {
    try {
      const parsed = JSON.parse(data);

      // Проверка на ошибку лимита соединений
      if (
        parsed.status === 'failure' &&
        isConnectionLimitError(parsed.message)
      ) {
        this.connectionLimitReached = true;
        const message = `[AppSocket] Лимит WebSocket соединений исчерпан: ${parsed.message}`;
        console.error(message);
        Sentry.captureMessage(message, 'warning');
        // Закрываем соединение чтобы onclose не начал переподключение
        this.ws?.close();

        return;
      }

      const event = parsed.event as string;

      // Обработка pong — сбрасываем таймаут
      if (parsed.message === 'pong') {
        this.debug && console.log('[AppSocket] <- pong');

        if (this.pongTimeout) {
          clearTimeout(this.pongTimeout);
          this.pongTimeout = null;
        }

        return;
      }

      const payload = parsed.payload;
      const handlers = this.listeners.get(event);

      handlers?.forEach((handler) => handler(payload));
    } catch (error) {
      this.debug && console.error('[AppSocket] parse ошибка:', error, data);
    }
  }

  /* Вызов колбеков при установлении соединения */
  onConnect(callback: () => void): () => void {
    if (this.isConnected) {
      callback();
    } else {
      this.connectCallbacks.add(callback);
    }

    return () => this.connectCallbacks.delete(callback);
  }
}
